import React, { useState } from "react";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";
import { Shield, ChevronDown, ChevronUp, Info } from "lucide-react";

interface CancellationProps {
  propertyData: PropertyToEdit | null;
}

const Cancellation: React.FC<CancellationProps> = ({ propertyData }) => {
  const [expanded, setExpanded] = useState(true);

  const htmlDescription = propertyData?.cancellation_policy?.description ?? "";
  const policyName = propertyData?.cancellation_policy?.name ?? "Standard policy";
  const hasContent = htmlDescription.trim().length > 0;

  return (
    <div className="listingSection__wrap w-full space-y-6">

      {/* Section header */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-3">
          <span className="block w-8 h-0.5 bg-[#F56948] rounded-full" />
          <span className="text-xs font-semibold uppercase tracking-widest text-[#F56948]">
            Policy
          </span>
        </div>
        <h2 className="text-2xl font-extrabold text-[#1A1A2E] dark:text-white tracking-tight">
          Cancellation Policy
        </h2>
        <p className="text-sm text-neutral-400 dark:text-neutral-500">
          Each property's cancellation policy is set by the host and may vary.
        </p>
      </div>

      <div className="h-px bg-neutral-100 dark:bg-neutral-800" />

      {/* Policy card */}
      <div className="border border-neutral-100 dark:border-neutral-800 rounded-2xl overflow-hidden bg-white dark:bg-neutral-900 shadow-sm">

        {/* Toggle header */}
        <button
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center justify-between p-5 hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#F56948]/10 flex items-center justify-center flex-shrink-0">
              <Shield size={18} className="text-[#F56948]" />
            </div>
            <div className="text-left">
              <h4 className="text-sm font-bold text-[#1A1A2E] dark:text-white">
                Cancellation policy
              </h4>
              <p className="text-xs text-neutral-400 mt-0.5 font-medium">{policyName}</p>
            </div>
          </div>
          <div className="w-7 h-7 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center flex-shrink-0">
            {expanded
              ? <ChevronUp size={15} className="text-neutral-500" />
              : <ChevronDown size={15} className="text-neutral-500" />
            }
          </div>
        </button>

        {/* Expanded body */}
        {expanded && (
          <div className="border-t border-neutral-100 dark:border-neutral-800">
            <div className="p-5">
              {hasContent ? (
                /*
                 * Renders the CKEditor HTML exactly as the admin formatted it.
                 * Tailwind [&_tag]: selectors restyle every element CKEditor
                 * can produce using Nomadroof brand colors.
                 *
                 * Security note: this HTML comes from your own admin CKEditor,
                 * not from end users, so dangerouslySetInnerHTML is safe here.
                 */
                <div
                  className="
                    ck-content prose prose-sm dark:prose-invert max-w-none
                    text-neutral-600 dark:text-neutral-400
                    [&_p]:mb-3 [&_p:last-child]:mb-0 [&_p]:leading-relaxed [&_p]:text-sm
                    [&_strong]:font-bold [&_strong]:text-[#1A1A2E] dark:[&_strong]:text-white
                    [&_em]:italic
                    [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_ul]:my-2
                    [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1.5 [&_ol]:my-2
                    [&_li]:text-sm [&_li]:leading-relaxed [&_li]:text-neutral-600 dark:[&_li]:text-neutral-400
                    [&_h2]:text-base [&_h2]:font-bold [&_h2]:text-[#1A1A2E] dark:[&_h2]:text-white [&_h2]:mt-4 [&_h2]:mb-2
                    [&_h3]:text-sm [&_h3]:font-bold [&_h3]:text-[#1A1A2E] dark:[&_h3]:text-white [&_h3]:mt-3 [&_h3]:mb-1
                    [&_blockquote]:border-l-2 [&_blockquote]:border-[#F56948] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-neutral-500 [&_blockquote]:my-3
                    [&_hr]:border-neutral-100 dark:[&_hr]:border-neutral-700 [&_hr]:my-4
                    [&_a]:text-[#F56948] [&_a]:underline [&_a:hover]:text-[#e0522f]
                  "
                  dangerouslySetInnerHTML={{ __html: htmlDescription }}
                />
              ) : (
                <p className="text-sm text-neutral-400 italic">
                  No cancellation policy description provided.
                </p>
              )}
            </div>

            {/* Note footer */}
            <div className="mx-5 mb-5 flex items-start gap-2.5 p-3.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-800/40">
              <Info size={15} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
                <span className="font-bold">Note: </span>
                Service fees are non-refundable. Review the full cancellation policy before booking.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cancellation;